import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { createTemplate } from "@/lib/api";

const MAX_NAME = 40;

export const SaveTemplateDialog = ({ draft, onClose, onSaved }) => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      setSaving(true);
      await createTemplate({ ...draft, name: trimmed });
      toast.success(t("tpl.saved"));
      onSaved?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t("tpl.failedSave"));
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div
      data-testid="save-template-dialog"
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/60"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={handleSave}
        className="relative bg-white brutal-border brutal-shadow w-full max-w-md p-6 font-mono"
      >
        <button
          type="button"
          data-testid="save-template-close"
          onClick={onClose}
          className="absolute top-3 right-3 bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive"
        >
          <X className="w-5 h-5" strokeWidth={3} />
        </button>
        <h3 className="font-display text-xl uppercase mb-4 pr-10">
          {t("tpl.saveTitle")}
        </h3>
        <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
          {t("tpl.nameLabel")}
        </label>
        <input
          autoFocus
          data-testid="save-template-name"
          value={name}
          maxLength={MAX_NAME}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("tpl.namePlaceholder")}
          className="w-full bg-white brutal-border p-3 text-base font-mono focus:outline-none focus:bg-[#FFF9B0] transition-colors"
        />
        <div className="text-right font-mono text-[10px] text-neutral-500 mt-1">
          {name.length}/{MAX_NAME}
        </div>
        <button
          type="submit"
          data-testid="save-template-submit"
          disabled={saving || !name.trim()}
          className="mt-3 w-full bg-black text-white brutal-border px-4 py-3 font-display uppercase text-base brutal-shadow brutal-interactive disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" strokeWidth={3} />
          {saving ? t("tpl.saving") : t("tpl.save")}
        </button>
      </form>
    </div>
  );

  if (typeof document === "undefined") return content;
  return createPortal(content, document.body);
};

export default SaveTemplateDialog;
